import PATH from '@/router/path';
import {
  DMP_APPLICATION,
  FREE_LEGAL_LINK_APPLICATION,
  findApplication,
  getApplicationEntryUrl,
  shouldSelectApplication,
} from '@/utils/applications';
import { getPblLawyerVettingStatus, hasPblRole } from '@/utils/pblRole';
import { safeInternalReturnPath } from '@/utils/safeReturnPath';
import { v2UiEnabled } from '@/components/DmpV2/features';
import { resolveDmpPurchaseReturnPath } from '@/components/DmpV2/purchaseReturnPath';

export const getPblReturnPath = (requestedPath?: string | null) => {
  const path = safeInternalReturnPath(requestedPath);
  if (!path?.startsWith('/pbl/')) return null;

  const searchParams = new URL(path, window.location.origin).searchParams;
  return searchParams.has('redirect_to') || searchParams.has('path') ? null : path;
};

export const resolveApplicationSelectionTarget = (application: UserApplicationItem, requestedPath?: string | null) =>
  (application.key === FREE_LEGAL_LINK_APPLICATION ? getPblReturnPath(requestedPath) : null) ||
  (application.key === DMP_APPLICATION && v2UiEnabled() ? resolveDmpPurchaseReturnPath(requestedPath) : null) ||
  getApplicationEntryUrl(application);

export const resolvePblLoginRedirect = (
  userData: any,
  applications?: UserApplicationContext,
  requestedPath?: string | null
) => {
  const returnPath = getPblReturnPath(requestedPath);
  const pendingPblSetup =
    userData?.pbl_citizen_profile?.password_setup_required ||
    (hasPblRole(userData, 'pbl_lawyer') && getPblLawyerVettingStatus(userData) !== 'approved');
  const dmpReturnPath =
    v2UiEnabled() && findApplication(applications, DMP_APPLICATION) && (requestedPath || !pendingPblSetup)
      ? resolveDmpPurchaseReturnPath(requestedPath, String(userData?.id || ''))
      : null;
  if (shouldSelectApplication(applications)) {
    const selectionPath = returnPath || dmpReturnPath;
    return selectionPath
      ? `${PATH.application.select}?path=${encodeURIComponent(selectionPath)}`
      : PATH.application.select;
  }
  if (dmpReturnPath) return dmpReturnPath;
  if (userData?.pbl_citizen_profile?.password_setup_required) {
    return returnPath
      ? `${PATH.pbl.passwordSetup}?redirect_to=${encodeURIComponent(returnPath)}`
      : PATH.pbl.passwordSetup;
  }

  if (hasPblRole(userData, 'pbl_lawyer') && getPblLawyerVettingStatus(userData) !== 'approved') {
    return PATH.pbl.lawyerApplicationStatus;
  }

  if (returnPath) return returnPath;

  const onlyApplication = applications?.available?.length === 1 ? applications.available[0] : null;
  if (onlyApplication) {
    return getApplicationEntryUrl(onlyApplication);
  }

  const pblApplication = findApplication(applications, 'free_legal_link');
  if (pblApplication && hasPblRole(userData, 'pbl_lawyer')) {
    return getApplicationEntryUrl(pblApplication);
  }

  return PATH.home.index;
};
